import {
  Box,
  CardContent,
  CheckerBoard,
  SuspenseImage
} from "@prismicio/editor-ui"
import type { ImageContent } from "@prismicio/types-internal/lib/content"
import type { Node as ProseMirrorNode } from "@tiptap/pm/model"
import { type NodeViewProps, NodeViewWrapper } from "@tiptap/react"
import { Suspense } from "react"

import {
  getImageLabel,
  getPreviewImage,
  MAIN_THUMBNAIL_NAME
} from "../../../../core/imageUtils"
import { EmptyCardContent } from "../../../ImageField/Components/EmptyCardContent"
import { ImageAltControl } from "../../../ImageField/Components/ImageAltControl"
import { ImageFieldCard } from "../../../ImageField/Components/ImageFieldCard"
import { useSelected } from "../useSelected"
import { useImageView } from "./useImageView"

interface ImageNode extends ProseMirrorNode {
  attrs: { content: ImageContent | undefined }
}
export interface ImageViewProps extends NodeViewProps {
  node: ImageNode
}

export function ImageView(props: ImageViewProps) {
  const { node } = props
  const content = node.attrs.content
  const { selected } = useSelected(props)

  const thumbnail = {
    width: undefined,
    height: undefined,
    name: MAIN_THUMBNAIL_NAME
  }

  const {
    onMediaSelected,
    onCropperDialogClose,
    isUpdating,
    updateContent,
    onClear
  } = useImageView({ content, thumbnail, imageViewProps: props })

  const isImageEmpty = content?.width === 0 && content?.height === 0

  const imageLabel =
    content && !isImageEmpty ? [`${content.width}x${content.height}px`] : []

  const contentAlt = content?.alt ?? ""

  const image = {
    thumbnail,
    contentView: content,
    labels: getImageLabel(thumbnail, content, content)
  }

  return (
    <NodeViewWrapper className="custom-image">
      <div contentEditable="false" suppressContentEditableWarning>
        <ImageFieldCard
          content={content}
          selected={selected}
          fieldLabel="Feature image"
          imageLabel={imageLabel}
          initialImages={[image]}
          readOnly={false}
          onMediaSelected={onMediaSelected}
          onCropperDialogClose={onCropperDialogClose}
          onClear={onClear}
        >
          <Suspense fallback={fallback}>
            {content?.url ? (
              <CardContent>
                <CheckerBoard>
                  <Box
                    flexDirection="column"
                    alignItems="center"
                    position="relative"
                    height={400}
                  >
                    <Box
                      justifyContent="center"
                      flexGrow={1}
                      maxHeight="100%"
                      maxWidth="100%"
                    >
                      <SuspenseImage
                        src={getPreviewImage(content.url)}
                        alt={contentAlt}
                        sizing="contain"
                      />
                    </Box>
                  </Box>
                </CheckerBoard>
                <ImageAltControl
                  onValueChange={(value) => {
                    updateContent({ ...content, alt: value })
                  }}
                  altText={contentAlt}
                  readOnly={!content}
                  selected={selected}
                />
              </CardContent>
            ) : (
              <EmptyCardContent
                isUpdating={isUpdating}
                readOnly={false}
                onMediaSelected={onMediaSelected}
              />
            )}
          </Suspense>
        </ImageFieldCard>
      </div>
    </NodeViewWrapper>
  )
}

//reserves height in-between loading images
const fallback = <div style={{ height: 468 }} />
